import React, {
  forwardRef,
  ReactChild,
  ReactElement,
  ReactFragment,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { WizardContextProvider } from "./context";
import { Step } from "./Step";
import { Dialog } from "../Dialog";

export interface IWizard {
  title: string;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  disableCloseOnEsc?: boolean;
  dialogClassName?: string;
}

export const Wizard = forwardRef<HTMLDivElement, IWizard>(
  (
    {
      title,
      open,
      onClose,
      disableCloseOnEsc = false,
      children,
      dialogClassName,
    },
    ref
  ) => {
    const childrenCollection = useMemo(
      () => React.Children.toArray(children),
      [children]
    );

    const [activeStep, setActiveStep] = useState(0);
    const [changingStepInProgress, setChangingStepInProgress] = useState(false);

    useEffect(() => {
      if (open) {
        setActiveStep(0);
      }
    }, [open]);

    const goNext = useCallback(() => {
      const index = childrenCollection.findIndex(
        (step: ReactChild | ReactFragment, index) => {
          const stepEl = step as ReactElement;
          return !stepEl?.props?.skip && index > activeStep;
        }
      );
      setActiveStep(index);
    }, [activeStep, childrenCollection]);

    const goPrevious = useCallback(() => {
      let index = 0;
      for (let i = childrenCollection.length - 1; i >= 0; i--) {
        const step = childrenCollection[i] as ReactElement;
        if (!step?.props?.skip && i < activeStep) {
          index = i;
          break;
        }
      }
      setActiveStep(index);
    }, [activeStep, childrenCollection]);

    const renderActiveStep = useMemo(() => {
      const element = childrenCollection[activeStep] as ReactElement;

      if (element?.type === Step) {
        return React.cloneElement(element, {
          index: activeStep,
          isLast: childrenCollection.length - 1 === activeStep,
          title,
          onNextButtonClick: goNext,
          onPreviousButtonClick: goPrevious,
          onClose,
          changingStepInProgress: changingStepInProgress,
        });
      }

      return null;
    }, [
      activeStep,
      changingStepInProgress,
      childrenCollection,
      goNext,
      goPrevious,
      onClose,
      title,
    ]);

    return (
      <WizardContextProvider
        value={{
          activeStep,
          setActiveStep,
          goNext,
          goPrevious,
          close: onClose,
          setChangingStepInProgress,
        }}
      >
        <Dialog
          ref={ref}
          open={open}
          onClose={onClose}
          disableCloseOnEsc={disableCloseOnEsc}
          className={dialogClassName}
        >
          {renderActiveStep}
        </Dialog>
      </WizardContextProvider>
    );
  }
);

Wizard.displayName = "Wizard";
